import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import type { MediaBox } from '../../hooks/useMediaBox';
import {
  usePreviewPan,
  usePreviewSetPanning,
  usePreviewZoom,
} from '../../state/preview-zoom.store';
import { clamp } from '../../utils/clamp';

const MINIMAP_MAX = 160;
const MINIMAP_MIN = 80;

interface Options {
  containerRef: RefObject<HTMLElement | null>;
  imageBox: MediaBox | null;
}

export interface MinimapView {
  miniW: number;
  miniH: number;
  rect: { left: number; top: number; width: number; height: number };
  handlers: {
    onPointerDown: (e: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerMove: (e: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerUp: (e: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerCancel: (e: ReactPointerEvent<HTMLButtonElement>) => void;
  };
}

export function useMinimapPreview({ containerRef, imageBox }: Options): MinimapView | null {
  const { zoom } = usePreviewZoom();
  const { pan, setPan } = usePreviewPan();
  const setPanning = usePreviewSetPanning();
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);

  const activePointerRef = useRef<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  const recenter = useCallback(
    (clientX: number, clientY: number, rect: DOMRect) => {
      if (!imageBox || !containerSize) return;
      const ib = imageBox;
      const cs = containerSize;
      const z = zoom;
      const imageX = ((clientX - rect.left) / rect.width) * ib.width;
      const imageY = ((clientY - rect.top) / rect.height) * ib.height;
      const nextPanX = (ib.width / 2 - imageX) * z;
      const nextPanY = (ib.height / 2 - imageY) * z;
      const overshootX = Math.max(0, (ib.width * z - cs.w) / 2);
      const overshootY = Math.max(0, (ib.height * z - cs.h) / 2);
      setPan({
        x: clamp(nextPanX, -overshootX, overshootX),
        y: clamp(nextPanY, -overshootY, overshootY),
      });
    },
    [imageBox, containerSize, zoom, setPan],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === 'touch') return;
      e.stopPropagation();
      if (e.button !== 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      e.currentTarget.setPointerCapture(e.pointerId);
      activePointerRef.current = e.pointerId;
      setPanning(true);
      recenter(e.clientX, e.clientY, rect);
    },
    [recenter, setPanning],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      const rect = e.currentTarget.getBoundingClientRect();
      recenter(e.clientX, e.clientY, rect);
    },
    [recenter],
  );

  const endPointer = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      activePointerRef.current = null;
      setPanning(false);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Pointer may already be released.
      }
    },
    [setPanning],
  );

  if (zoom <= 1 || !imageBox || !containerSize) return null;
  if (imageBox.width === 0 || imageBox.height === 0) return null;

  const aspect = imageBox.width / imageBox.height;
  const miniW = aspect >= 1 ? MINIMAP_MAX : Math.max(MINIMAP_MIN, MINIMAP_MAX * aspect);
  const miniH = aspect >= 1 ? Math.max(MINIMAP_MIN, MINIMAP_MAX / aspect) : MINIMAP_MAX;

  const viewportW = Math.min(imageBox.width, containerSize.w / zoom);
  const viewportH = Math.min(imageBox.height, containerSize.h / zoom);
  const viewportCx = imageBox.width / 2 - pan.x / zoom;
  const viewportCy = imageBox.height / 2 - pan.y / zoom;

  const sx = miniW / imageBox.width;
  const sy = miniH / imageBox.height;

  return {
    miniW,
    miniH,
    rect: {
      left: (viewportCx - viewportW / 2) * sx,
      top: (viewportCy - viewportH / 2) * sy,
      width: viewportW * sx,
      height: viewportH * sy,
    },
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
    },
  };
}
